import React, { useState } from 'react';
import { View } from 'react-native';
import { CURRENT_USER, dmChannel } from '@ventureroom/core';
import { useApp, useData } from './provider';
import {
  Avatar,
  Button,
  Card,
  Column,
  Copy,
  Empty,
  Heading,
  Page,
  Row,
  Segments,
  Tag,
  dateLabel,
} from './components';

export function Connections() {
  const data = useData();
  const { navigate, act, busy } = useApp();
  const [tab, setTab] = useState('Chats');
  const incoming = data.connections.filter((c) => c.to === CURRENT_USER && c.status === 'pending');
  const chats = data.connections
    .filter((c) => (c.status === 'accepted' || (c.status === 'pending' && c.from === CURRENT_USER)) && [c.from, c.to].includes(CURRENT_USER))
    .flatMap((connection) => {
      const person = data.profiles.find(
        (p) => p.id === (connection.from === CURRENT_USER ? connection.to : connection.from),
      );
      if (!person) return [];
      const last = data.messages
        .filter((m) => m.channel === dmChannel(CURRENT_USER, person.id))
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
      const ventures = data.ventures.filter(
        (v) => v.members.includes(CURRENT_USER) && v.members.includes(person.id),
      );
      return [{ person, last, ventures, updated: last?.createdAt ?? connection.createdAt, connection }];
    })
    .sort((a, b) => {
      if (a.person.id === 'ai-assistant') return -1;
      if (b.person.id === 'ai-assistant') return 1;
      return b.updated.localeCompare(a.updated);
    });
  return (
    <Page title="Chats">
      <Segments
        items={['Chats', `Likes you (${incoming.length})`]}
        value={tab === 'Likes you' ? `Likes you (${incoming.length})` : tab}
        onChange={(value) => setTab(value.startsWith('Likes you') ? 'Likes you' : value)}
      />
      {tab === 'Chats' ? (
        <Column gap={16}>
          {data.profiles
            .filter((p) => p.subscriptionTier === 'agency' && (p.id === CURRENT_USER || p.team?.includes(CURRENT_USER)))
            .map((owner) => {
              const last = data.messages
                .filter((m) => m.channel === `team:${owner.id}`)
                .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
              const text = last
                ? `${last.author === CURRENT_USER ? 'You: ' : ((data.profiles.find(p => p.id === last.author)?.name.split(' ')[0] ?? '') + ': ')}${last.text.replace(/\\s+/g, ' ')}`
                : 'Team meeting room.';
              const preview = text.length > 120 ? `${text.slice(0, 120).trimEnd()}…` : text;
              return (
                <Card
                  key={`meeting-${owner.id}`}
                  label={`${owner.name}'s Meeting Room`}
                  onPress={() => navigate(`meeting/${owner.id}`)}
                  style={{ paddingVertical: 18, borderColor: '#b91c1c' }}
                >
                  <Row style={{ alignItems: 'flex-start', gap: 14 }}>
                    <Avatar person={owner} size={52} />
                    <Column gap={5} style={{ flex: 1, minWidth: 0 }}>
                      <Row between>
                        <View style={{ flex: 1 }}>
                          <Heading size={20}>{owner.id === CURRENT_USER ? 'Your' : owner.name.split(' ')[0] + "'s"} Team</Heading>
                        </View>
                        <Copy muted size={10}>
                          {last ? dateLabel(last.createdAt) : ''}
                        </Copy>
                      </Row>
                      <Copy bold size={12} lines={1} style={{ color: '#b91c1c' }}>
                        Meeting Room
                      </Copy>
                      <Copy muted size={13} lines={2}>
                        {preview}
                      </Copy>
                    </Column>
                    <Copy muted size={24}>
                      ›
                    </Copy>
                  </Row>
                </Card>
              );
            })}
          {chats.length ? (
          chats.map(({ person, last, ventures, updated, connection }) => {
            const text = last
              ? `${last.author === CURRENT_USER ? 'You: ' : ''}${last.text.replace(/\s+/g, ' ')}`
              : connection.status === 'pending' ? 'Start collaborating with this person.' : 'You matched. Say hello!';
            const preview = text.length > 120 ? `${text.slice(0, 120).trimEnd()}…` : text;
            return (
              <Card
                key={person.id}
                label={`Chat with ${person.name}`}
                onPress={() => navigate(`chat/${person.id}`)}
                style={{ paddingVertical: 18 }}
              >
                <Row style={{ alignItems: 'flex-start', gap: 14 }}>
                  <Avatar person={person} size={52} />
                  <Column gap={5} style={{ flex: 1, minWidth: 0 }}>
                    <Row between>
                      <View style={{ flex: 1 }}>
                        <Heading size={20}>{person.name}</Heading>
                      </View>
                      <Copy muted size={10}>
                        {dateLabel(updated)}
                      </Copy>
                    </Row>
                    {!!ventures.length && (
                      <Copy bold size={12} lines={1}>
                        {ventures.map((v) => v.name).join(' · ')}
                      </Copy>
                    )}
                    <Copy muted size={13} lines={2}>
                      {preview}
                    </Copy>
                  </Column>
                  <Copy muted size={24}>
                    ›
                  </Copy>
                </Row>
              </Card>
            );
          })
        ) : (
          <Empty
            title="No matches yet"
            detail="Like someone in Discover. When they like you back, your chat appears here."
            action={<Button onPress={() => navigate('discover')}>Discover people</Button>}
          />
        )}
        </Column>
      ) : incoming.length ? (
        incoming.map((c) => {
          const person = data.profiles.find((p) => p.id === c.from)!;
          return (
            <Card key={c.id}>
              <Row>
                <Avatar person={person} />
                <Column gap={4}>
                  <Heading size={20}>{person.name}</Heading>
                  <Copy muted size={13}>
                    {person.background}
                  </Copy>
                </Column>
              </Row>
              <Tag>Likes you</Tag>
              {!!c.intro && <Copy>{c.intro}</Copy>}
              <Row wrap>
                <Button
                  disabled={busy}
                  onPress={() =>
                    void act(
                      { type: 'connect', personId: person.id, intro: '' },
                      'It’s a match! Open Chats to say hello.',
                    )
                  }
                >
                  Like back
                </Button>
                <Button
                  disabled={busy}
                  kind="secondary"
                  onPress={() =>
                    void act({ type: 'bookmark', personId: person.id, state: 'passed' }, 'Passed')
                  }
                >
                  Pass
                </Button>
                <Button kind="quiet" onPress={() => navigate(`person/${person.id}`)}>
                  View profile
                </Button>
              </Row>
            </Card>
          );
        })
      ) : (
        <Empty
          title="No new likes"
          detail="When someone likes your profile, you can like them back here or in Discover."
        />
      )}
    </Page>
  );
}
